import { useEffect, useRef } from 'react';
import { doc, updateDoc, getDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';

/**
 * useActivityTracker - Tracks user activity for the Active Users Dashboard
 * 
 * Call this hook in your main App component or authenticated layout.
 * It will:
 * - Set sessionStart when user first loads the app
 * - Update lastActivity every 60 seconds while user is active
 * - Update lastActivity on user interactions (clicks, keypress, scroll)
 * - Track which days this week the user has logged in
 * 
 * Usage:
 *   import { useActivityTracker } from './utils/useActivityTracker';
 *   
 *   function App() {
 *     const { user } = useAuth(); // Your auth hook
 *     useActivityTracker(user?.uid);
 *     // ... rest of app
 *   }
 */

/**
 * Get the Monday of the current week (for week tracking reset)
 */
const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get day of week as 0-6 (Monday = 0, Sunday = 6)
 */
const getDayOfWeek = (date = new Date()) => {
  const day = date.getDay();
  return day === 0 ? 6 : day - 1; // Convert: Sun=0 -> 6, Mon=1 -> 0, etc.
};

export const useActivityTracker = (userId) => {
  const lastUpdateRef = useRef(0);
  const sessionStartedRef = useRef(false);
  
  useEffect(() => {
    if (!userId) return;
    
    const userRef = doc(db, 'users', userId);
    
    // Set session start on first load
    const setSessionStart = async () => {
      if (sessionStartedRef.current) return;
      sessionStartedRef.current = true;
      
      try {
        // Get current week start for comparison
        const currentWeekStart = getWeekStart();
        const todayIndex = getDayOfWeek();
        
        // Get existing user data to check if we need to reset the week
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data() || {};
        
        let loginDaysThisWeek = userData.loginDaysThisWeek || [];
        let storedWeekStart = userData.weekStart;
        
        // Parse stored week start
        if (storedWeekStart) {
          if (storedWeekStart.toDate) {
            storedWeekStart = storedWeekStart.toDate();
          } else if (storedWeekStart._seconds) {
            storedWeekStart = new Date(storedWeekStart._seconds * 1000);
          } else {
            storedWeekStart = new Date(storedWeekStart);
          }
        }
        
        // Check if we need to reset for a new week
        const isNewWeek = !storedWeekStart || 
          storedWeekStart.getTime() < currentWeekStart.getTime();
        
        if (isNewWeek) {
          // New week - reset the array with just today
          loginDaysThisWeek = [todayIndex];
        } else if (!loginDaysThisWeek.includes(todayIndex)) {
          // Same week - add today if not already tracked
          loginDaysThisWeek = [...loginDaysThisWeek, todayIndex].sort((a, b) => a - b);
        }
        
        await updateDoc(userRef, {
          sessionStart: serverTimestamp(),
          lastActivity: serverTimestamp(),
          lastLogin: serverTimestamp(),
          loginDaysThisWeek: loginDaysThisWeek,
          weekStart: currentWeekStart
        });
        
        console.log('📍 Session started, login days this week:', loginDaysThisWeek);
      } catch (error) {
        console.error('Error setting session start:', error);
      }
    };
    
    // Update lastActivity (throttled to once per 60 seconds)
    const updateActivity = async () => {
      const now = Date.now();
      if (now - lastUpdateRef.current < 60000) return; // Throttle to 1 min
      
      lastUpdateRef.current = now;
      
      try {
        await updateDoc(userRef, {
          lastActivity: serverTimestamp()
        });
      } catch (error) {
        // Silently fail - not critical
      }
    };
    
    // Start session
    setSessionStart();
    
    // Set up activity listeners
    const events = ['click', 'keypress', 'scroll', 'mousemove'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
    
    // Also update on interval (in case user is just reading)
    const interval = setInterval(updateActivity, 60000);
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(interval);
    };
  }, [userId]);
};

/**
 * Alternative: Simple function to call manually
 * Use this if you prefer to call it yourself at specific points
 */
export const updateUserActivity = async (userId) => {
  if (!userId) return;
  
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      lastActivity: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating activity:', error);
  }
};

/**
 * Track login - call this when user logs in
 * This also records to loginDaysThisWeek for the "days this week" metric
 */
export const trackUserLogin = async (userId) => {
  if (!userId) return;
  
  try {
    const userRef = doc(db, 'users', userId);
    const currentWeekStart = getWeekStart();
    const todayIndex = getDayOfWeek();
    
    // Get existing user data
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data() || {};
    
    let loginDaysThisWeek = userData.loginDaysThisWeek || [];
    let storedWeekStart = userData.weekStart;
    
    // Parse stored week start
    if (storedWeekStart) {
      if (storedWeekStart.toDate) {
        storedWeekStart = storedWeekStart.toDate();
      } else if (storedWeekStart._seconds) {
        storedWeekStart = new Date(storedWeekStart._seconds * 1000);
      } else {
        storedWeekStart = new Date(storedWeekStart);
      }
    }
    
    // Check if we need to reset for a new week
    const isNewWeek = !storedWeekStart || 
      storedWeekStart.getTime() < currentWeekStart.getTime();
    
    if (isNewWeek) {
      loginDaysThisWeek = [todayIndex];
    } else if (!loginDaysThisWeek.includes(todayIndex)) {
      loginDaysThisWeek = [...loginDaysThisWeek, todayIndex].sort((a, b) => a - b);
    }
    
    await updateDoc(userRef, {
      lastLogin: serverTimestamp(),
      sessionStart: serverTimestamp(),
      lastActivity: serverTimestamp(),
      loginDaysThisWeek: loginDaysThisWeek,
      weekStart: currentWeekStart
    });
    
    console.log('📍 Login tracked, days this week:', loginDaysThisWeek);
  } catch (error) {
    console.error('Error tracking login:', error);
  }
};
